import { forwardRef, useImperativeHandle, useRef } from "react";
import { DCTariff } from "./DCTariff";
import {
  useTariffSnackbar,
  TariffSnackbar,
  validateTimeSlotsContiguous,
  validateWeekdaysCoverage,
} from "../utils/tariffValidationUtils";

const DemandCharge = forwardRef((props, ref) => {
  const mgTariffRef = useRef();
  const { snackbarId, snackbarProps, callSnackbar } = useTariffSnackbar("Success");

  const handleValidate = () => {
    const jsonData = mgTariffRef.current.getJsonData();
    if (validateJsonData(jsonData)) {
      console.log("Validation passed");
      return { error: false, message: "Validation passed", data: jsonData };
    } else {
      return { error: true, message: "Validation failed" };
    }
  };

  const validateJsonData = (jsonData) => {
    if (!jsonData || jsonData.length === 0) return false;

    for (let i = 0; i < jsonData.length; i++) {
      const season = jsonData[i];

      // Check NC cost validation (specific to DemandCharge)
      if (season.isNcCost && (season.ncCost === undefined || season.ncCost === null || season.ncCost === "")) {
        callSnackbar(
          "error",
          `Non-Coincidental Cost is enabled for ${season.name || `Season ${i + 1}`}, but no Non-Coincidental Cost value is entered. Please enter a value for Non-Coincidental Cost.`
        );
        return false;
      }

      for (let j = 0; j < season.periods.length; j++) {
        const dayGroup = season.periods[j];
        const allTimeSlots = dayGroup.rate.flatMap((tariff) => tariff.timeSlots);

        // Filter out empty time slots (start: 0:00, end: 0:00)
        const validTimeSlots = allTimeSlots.filter((slot) => {
          const isEmptySlot =
            slot.startTime.hour === 0 &&
            slot.startTime.minute === 0 &&
            slot.endTime.hour === 0 &&
            slot.endTime.minute === 0;
          return !isEmptySlot;
        });

        // Check if valid time slots exist
        if (validTimeSlots.length === 0) {
          callSnackbar(
            "error",
            `No valid time slots found for ${dayGroup?.demandTypeName}. Please ensure every period in this group has at least one time slot defined.`
          );
          return false;
        }

        // Validate contiguous time slots
        const contiguousResult = validateTimeSlotsContiguous(validTimeSlots);
        if (!contiguousResult.valid) {
          callSnackbar(
            "error",
            `Time slots for ${dayGroup?.demandTypeName} are not continuous. Please ensure all time slots are contiguous and non-overlapping for every period in this group.`
          );
          return false;
        }

        // Check if time slots cover full 24 hours (0:00 to 24:00)
        if (validTimeSlots.length > 0) {
          const sortedSlots = [...validTimeSlots].sort((a, b) => {
            const startA = a.startTime.hour * 60 + a.startTime.minute;
            const startB = b.startTime.hour * 60 + b.startTime.minute;
            return startA - startB;
          });
          const firstSlot = sortedSlots[0];
          const lastSlot = sortedSlots[sortedSlots.length - 1];
          const startsAtZero = firstSlot.startTime.hour === 0 && firstSlot.startTime.minute === 0;
          const endsAt24 = lastSlot.endTime.hour === 24 && lastSlot.endTime.minute === 0;

          if (!startsAtZero || !endsAt24) {
            callSnackbar(
              "error",
              `Time slots for ${dayGroup?.demandTypeName} do not cover the full 24-hour period. Please ensure time slots span from 00:00 to 24:00.`
            );
            return false;
          }
        }
      }

      // Validate weekdays coverage
      if (!validateWeekdaysCoverage(season.periods)) {
        callSnackbar("error", "Each season must cover all 7 weekdays. Please ensure every weekday is included in all seasons.");
        return false;
      }
    }

    return true;
  };

  useImperativeHandle(ref, () => ({
    handleValidate,
  }));

  const { type, message } = snackbarProps;
  return (
    <>
      <TariffSnackbar id={snackbarId} type={type} message={message} />
      <div className="container-fluid" style={{ paddingTop: "15px", paddingBottom: "15px", overflow: "auto" }}>
        <div className="row">
          <div className="col-md-12">
            <DCTariff
              ref={mgTariffRef}
              tariffTemplate={
                props.tariffTemplate?.length ? props.tariffTemplate : null
              }
              importTariffSeasons={props.importTariffSeasons || null}
              getImportTariffSeasons={props.getImportTariffSeasons}
              isVersionCompleted={props.isVersionCompleted}
              onChange={props?.onChange}
            />
          </div>
        </div>
      </div>
    </>
  );
});

export default DemandCharge;
